import React, { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import TopNav from "../../components/navigation/TopNav";
import TimeRangeSelector from "../../components/helpers/TimeRangeSelector";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import StatusMessage from "../../components/ui/StatusMessage";

import {
  TableShell,
  Table,
  Th,
  Td,
  NumberTd,
} from "../../components/data/Table";

const narrowShellStyle = {
  width: "100%",
  margin: "0 auto",
};

export default function PayerToFromVendorsReport() {
  const { t } = useTranslation();

  const [summary, setSummary] = useState([]);
  const [message, setMessage] = useState("");
  const [messageVariant, setMessageVariant] = useState("info");
  const [timeRange, setTimeRange] = useState({ start: "", end: "" });
  const [loading, setLoading] = useState(false);

  const formatDateTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "-";

    return date
      .toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  };

  const translateNote = (value) => {
    if (!value) return "-";
    return t(`payer_to_from_vendors_report.note_values.${value}`, value);
  };

  const getWalletRoute = (item) => {
    return (
      item.to_walletroute ||
      item.from_walletroute ||
      item.route_name ||
      "-"
    );
  };

  const fetchReport = async () => {
    if (!timeRange.start || !timeRange.end) {
      setMessage(t("payer_to_from_vendors_report.messages.missing_dates"));
      setMessageVariant("error");
      return;
    }

    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;

    setLoading(true);
    setMessage("");
    setMessageVariant("info");

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/payer/to-from-vendors-in-time`,
        {
          params: {
            start: timeRange.start,
            end: timeRange.end,
          },
        }
      );

      const data = Array.isArray(res.data) ? res.data : [];
      setSummary(data);

      if (data.length === 0) {
        setMessage(t("payer_to_from_vendors_report.messages.empty"));
        setMessageVariant("info");
      }
    } catch (err) {
      console.error("Failed to fetch payer ticket history", err);
      setSummary([]);
      setMessage(
        err?.response?.data?.detail ||
          t("payer_to_from_vendors_report.messages.fetch_failed")
      );
      setMessageVariant("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <StandardScreen
      topNav={<TopNav title={t("payer_to_from_vendors_report.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">
            <Card title={t("payer_to_from_vendors_report.title")}>
              <Stack gap="3">
                <FormField>
                  <TimeRangeSelector onRangeChange={setTimeRange} />
                </FormField>

                <Button onClick={fetchReport} disabled={loading} fullWidth>
                  {loading
                    ? t("payer_to_from_vendors_report.actions.loading")
                    : t("payer_to_from_vendors_report.actions.get_report")}
                </Button>

                <StatusMessage variant={messageVariant} message={message} />
              </Stack>
            </Card>

            {summary.length > 0 && (
              <Card>
                <TableShell maxHeight="65vh">
                  <Table>
                    <thead>
                      <tr>
                        <Th columnIndex={0} sticky wider>
                          {t("payer_to_from_vendors_report.table.created_at")}
                        </Th>

                        <Th columnIndex={1} sticky wider>
                          {t("payer_to_from_vendors_report.table.action")}
                        </Th>

                        <Th wider>
                          {t("payer_to_from_vendors_report.table.wallet")}
                        </Th>

                        <Th wider>
                          {t("payer_to_from_vendors_report.table.fullname")}
                        </Th>

                        <Th wider>
                          {t("payer_to_from_vendors_report.table.userno")}
                        </Th>

                        <Th wider>
                          {t("payer_to_from_vendors_report.table.sponsor")}
                        </Th>

                        <Th align="right">
                          {t("payer_to_from_vendors_report.table.tickets")}
                        </Th>
                      </tr>
                    </thead>

                    <tbody>
                      {summary.map((item, index) => (
                        <tr key={`${item.treq_id || item.created_at}-${index}`}>
                          <Td columnIndex={0} sticky wider>
                            {formatDateTime(item.created_at)}
                          </Td>

                          <Td columnIndex={1} sticky wider>
                            {t(`payer_to_from_vendors_report.table.${item.request}`)}
                          </Td>

                          <Td wider>
                            {getWalletRoute(item)}
                          </Td>

                          <Td>
                            {item.payer_fullname || "-"}
                          </Td>

                          <Td>
                            {item.payer_userno || "-"}
                          </Td>

                          <Td>
                            {item.payer_sponsor || "-"}
                          </Td>

                          <NumberTd>
                            {item.payer_delta_tickets ?? 0}
                          </NumberTd>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableShell>
              </Card>
            )}
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}